
import React from 'react';
import { PlayerProfile, PlayerInjury, PlayerTransfer, PlayerAchievement, PlayerStats } from '../../types/player';
import { cn, formatCurrency } from '../../lib/utils';
import { ArrowRightCircle, ShieldAlert, Trophy } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface TimelineEvent {
  id: string;
  type: 'transfer' | 'injury' | 'achievement';
  date: Date;
  data: PlayerTransfer | PlayerInjury | PlayerAchievement;
}

interface HorizontalCareerTimelineProps {
  playerProfile: PlayerProfile;
  playerTransfers: PlayerTransfer[] | null;
  playerInjuries: PlayerInjury[] | null;
  playerAchievements: PlayerAchievement[] | null;
  playerStats: PlayerStats[] | null;
}

export const HorizontalCareerTimeline: React.FC<HorizontalCareerTimelineProps> = ({
  playerProfile,
  playerTransfers,
  playerInjuries,
  playerAchievements,
  playerStats,
}) => {
  const events: TimelineEvent[] = [
    ...(playerTransfers || []).map(transfer => ({
      id: transfer.id,
      type: 'transfer' as const,
      date: new Date(transfer.transfer_date),
      data: transfer,
    })),
    ...(playerInjuries || []).map((injury, index) => ({
      id: `injury-${index}`,
      type: 'injury' as const,
      date: new Date(injury.from_date),
      data: injury,
    })),
    ...(playerAchievements || []).map(achievement => {
      const season = achievement.season_name;
      let year;

      if (season.includes('/')) {
        let yearStr = season.split('/')[1];
        if (yearStr.length === 2) {
          yearStr = '20' + yearStr;
        }
        year = parseInt(yearStr, 10);
      } else {
        year = parseInt(season, 10);
      }

      const date = !isNaN(year) ? new Date(`${year}-06-01`) : new Date();

      return {
        id: achievement.id,
        type: 'achievement' as const,
        date: date,
        data: achievement,
      };
    }),
  ].sort((a, b) => a.date.getTime() - b.date.getTime());

  if (events.length === 0) {
    return (
      <div className="text-center text-sm text-[#CECFD2] opacity-70 py-4">
        No career events to display.
      </div>
    );
  }

  const renderEventCard = (event: TimelineEvent, isLatestTransfer: boolean) => {
    if (event.type === 'transfer') {
      const transfer = event.data as PlayerTransfer;
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="bg-[#26222E] p-3 rounded-lg shadow-lg border border-gray-500 w-64 cursor-pointer">
                <div className="flex items-center mb-2">
                  <ArrowRightCircle className="h-5 w-5 text-[#4E46FF] mr-2" />
                  <h5 className="font-bold text-[#CECFD2] text-sm">
                    {transfer.is_loan_end ? 'End of loan' : transfer.is_loan ? 'Loan' : 'Transfer'}
                  </h5>
                </div>
                <p className="text-xs text-[#CECFD2]">
                  <span className="font-medium">{transfer.from_club_name}</span>
                  <span className="mx-1">→</span>
                  <span className="font-medium">{transfer.to_club_name}</span>
                </p>
                <div className="text-xs text-gray-400 mt-2">
                  {event.date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-[#121212] text-white border-gray-700">
              <p>Fee: {formatCurrency(transfer.fee)}</p>
              <p>Market Value: {formatCurrency(transfer.market_value)}</p>
              {isLatestTransfer && playerProfile.contract_expires && (
                <p>Contract until: {playerProfile.contract_expires}</p>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    if (event.type === 'injury') {
      const injury = event.data as PlayerInjury;
      const isActiveInjury = !injury.until_date || new Date(injury.until_date) > new Date();
      return (
        <div className={`bg-[#26222E] p-3 rounded-lg shadow-lg border ${isActiveInjury ? 'border-red-500' : 'border-gray-500'} w-64`}>
          <div className="flex items-center mb-2">
            <ShieldAlert className={`h-5 w-5 ${isActiveInjury ? 'text-red-500' : 'text-yellow-500'} mr-2`} />
            <h5 className="font-bold text-[#CECFD2] text-sm">Injury</h5>
          </div>
          <p className="text-xs text-[#CECFD2]">{injury.injury}</p>
          <p className="text-xs text-gray-400 mt-1">
            {injury.days} days out ({injury.games_missed} games missed)
          </p>
          <div className="text-xs text-gray-400 mt-2">
            {event.date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
        </div>
      );
    }

    if (event.type === 'achievement') {
      const achievement = event.data as PlayerAchievement;
      const seasonStats = playerStats
        ?.filter(s => s.season_id === achievement.season_name)
        .reduce((acc, s) => {
          acc.goals += s.goals;
          acc.assists += s.assists;
          acc.appearances += s.appearances;
          acc.minutes_played += s.minutes_played;
          return acc;
        }, { goals: 0, assists: 0, appearances: 0, minutes_played: 0 });

      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="bg-[#26222E] p-3 rounded-lg shadow-lg border border-yellow-500 w-64 cursor-pointer">
                <div className="flex items-center mb-2">
                  <Trophy className="h-5 w-5 text-yellow-500 mr-2" />
                  <h5 className="font-bold text-[#CECFD2] text-sm">Achievement</h5>
                </div>
                <p className="text-xs text-[#CECFD2]">{achievement.achievement_title}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {achievement.competition_name}
                </p>
                <div className="text-xs text-gray-400 mt-2">
                  Season {achievement.season_name}
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-[#121212] text-white border-gray-700 z-50">
              <p>Club: {achievement.club_name}</p>
              {seasonStats && seasonStats.appearances > 0 ? (
                <>
                  <p>Appearances: {seasonStats.appearances}</p>
                  <p>Goals: {seasonStats.goals}</p>
                  <p>Assists: {seasonStats.assists}</p>
                  <p>Minutes played: {seasonStats.minutes_played}</p>
                </>
              ) : (
                <p>No stats available for this season.</p>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return null;
  };

  return (
    <div className="w-full overflow-x-auto py-8 px-4 bg-[#121212]">
      <div className="relative flex items-center" style={{ minWidth: `${events.length * 20}rem`, height: '20rem' }}>
        {/* Timeline Axis */}
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gray-600" />

        {events.map((event, index) => {
          const isAbove = index % 2 === 0;
          const verticalOffset = Math.random() * 1.5; // 0 to 1.5rem
          const isLatestTransfer = event.type === 'transfer' && index === events.length - 1;

          return (
            <div key={event.id} className="flex-1 flex justify-center">
              <div className="relative flex flex-col items-center">
                {/* Event Card - Alternate above and below */}
                <div
                  className={cn('absolute', isAbove ? 'bottom-full mb-4' : 'top-full mt-4')}
                  style={{
                    transform: isAbove
                      ? `translateY(-${verticalOffset}rem)`
                      : `translateY(${verticalOffset}rem)`,
                  }}
                >
                  {renderEventCard(event, isLatestTransfer)}
                </div>

                {/* Timeline Dot */}
                <div className="w-3 h-3 bg-[#4E46FF] rounded-full z-10 border-2 border-[#121212]" />

                {/* Date Label */}
                <div className={cn('absolute text-xs text-gray-400', isAbove ? 'top-full mt-2' : 'bottom-full mb-2')}>
                  {event.date.getFullYear()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
